import React from "react";
import { FaBed, FaLock, FaCheckCircle, FaCreditCard } from "react-icons/fa";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AnimatePresence } from "framer-motion";
import {
  BillablePlanKey,
  STRIPE_PLAN_USD_MONTHLY,
  formatUsdWhole,
  monthlyTotalFromSelections,
  prepayTotalFromSelections,
  threeYearPrepayFromMonthly,
} from "../config/stripePlanPricing";

const fmt = (n: number) => formatUsdWhole(n);

const pricingTiers = [
  {
    name: "Small",
    bedRange: "4–50 beds",
    baseMonthly: fmt(STRIPE_PLAN_USD_MONTHLY.small.base),
    maintenance: fmt(STRIPE_PLAN_USD_MONTHLY.small.maintenance),
    support: fmt(STRIPE_PLAN_USD_MONTHLY.small.support),
    setup: "$500",
    icon: <FaBed className="text-blue-500 text-3xl mb-2" />,
    popular: false,
    key: "small",
  },
  {
    name: "Medium",
    bedRange: "51–80 beds",
    baseMonthly: fmt(STRIPE_PLAN_USD_MONTHLY.medium.base),
    maintenance: fmt(STRIPE_PLAN_USD_MONTHLY.medium.maintenance),
    support: fmt(STRIPE_PLAN_USD_MONTHLY.medium.support),
    setup: "$1000",
    icon: (
      <div className="flex gap-1 mb-2 text-blue-600">
        <FaBed className="text-3xl" />
        <FaBed className="text-3xl" />
      </div>
    ),
    popular: true,
    key: "medium",
  },
  {
    name: "Large-Medium",
    bedRange: "81–125 beds",
    baseMonthly: fmt(STRIPE_PLAN_USD_MONTHLY["large-medium"].base),
    maintenance: fmt(STRIPE_PLAN_USD_MONTHLY["large-medium"].maintenance),
    support: fmt(STRIPE_PLAN_USD_MONTHLY["large-medium"].support),
    setup: "$1500",
    icon: (
      <div className="flex gap-1 mb-2 text-blue-400">
        <FaBed className="text-3xl" />
        <FaBed className="text-3xl" />
        <FaBed className="text-3xl" />
      </div>
    ),
    popular: false,
    key: "large-medium",
  },
  {
    name: "Large",
    bedRange: "126–200 beds",
    baseMonthly: fmt(STRIPE_PLAN_USD_MONTHLY.large.base),
    maintenance: fmt(STRIPE_PLAN_USD_MONTHLY.large.maintenance),
    support: fmt(STRIPE_PLAN_USD_MONTHLY.large.support),
    setup: "$2000",
    icon: (
      <div className="flex gap-1 mb-2 text-blue-700">
        <FaBed className="text-3xl" />
        <FaBed className="text-3xl" />
        <FaBed className="text-3xl" />
        <FaBed className="text-3xl" />
      </div>
    ),
    popular: false,
    key: "large",
  },
  {
    name: "Enterprise",
    bedRange: "201+ beds",
    baseMonthly: "-",
    maintenance: "-",
    support: "-",
    setup: "-",
    icon: (
      <div className="flex gap-1 mb-2 text-blue-800 flex-wrap justify-center max-w-[11rem]">
        <FaBed className="text-3xl" />
        <FaBed className="text-3xl" />
        <FaBed className="text-3xl" />
        <FaBed className="text-3xl" />
        <FaBed className="text-3xl" />
      </div>
    ),
    popular: false,
    key: "enterprise",
  },
];

const cardVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 40 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 0.6, type: "spring" as const },
  },
};

const buttonTap = { scale: 0.95 };
const buttonHover = { scale: 1.05 };

const Pricing: React.FC = () => {
  const [selectedTierKey, setSelectedTierKey] = React.useState<string | null>(
    null
  );
  const [modalBilling, setModalBilling] = React.useState<
    "monthly" | "prepay"
  >("monthly");
  const [cardBilling, setCardBilling] = React.useState<
    Record<string, "monthly" | "prepay">
  >({
    small: "monthly",
    medium: "monthly",
    "large-medium": "monthly",
    large: "monthly",
  });
  const [includeMaintenance, setIncludeMaintenance] =
    React.useState<boolean>(false);
  const [includeSupport, setIncludeSupport] = React.useState<boolean>(false);
  const [freeTrialModalOpen, setFreeTrialModalOpen] = React.useState(false);
  const [trialPlanKey, setTrialPlanKey] = React.useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Open modal from query param if /pricing?freeTrialOpen=true
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("freeTrialOpen") === "true") {
      setFreeTrialModalOpen(true);
    }
  }, [location.search]);

  const selectedTier = React.useMemo(
    () => pricingTiers.find((t) => t.key === selectedTierKey) || null,
    [selectedTierKey]
  );

  const totalMonthly = React.useMemo(() => {
    if (!selectedTier || selectedTier.key === "enterprise") return 0;
    return monthlyTotalFromSelections({
      planKey: selectedTier.key,
      includeMaintenance,
      includeSupport,
    });
  }, [selectedTier, includeMaintenance, includeSupport]);

  const prepayThreeYearTotal = React.useMemo(() => {
    if (!selectedTier || selectedTier.key === "enterprise") return 0;
    return prepayTotalFromSelections({
      planKey: selectedTier.key,
      includeMaintenance,
      includeSupport,
    });
  }, [selectedTier, includeMaintenance, includeSupport]);

  const openModal = (tierKey: string) => {
    setSelectedTierKey(tierKey);
    setModalBilling(cardBilling[tierKey] ?? "monthly");
    setIncludeMaintenance(false);
    setIncludeSupport(false);
  };

  const closeModal = () => {
    setSelectedTierKey(null);
  };

  const buildPaymentUrl = (): string => {
    if (!selectedTier) return "/payment";
    const params = new URLSearchParams({ plan: selectedTier.key });
    if (includeMaintenance) params.set("maintenance", "true");
    if (includeSupport) params.set("support", "true");
    if (modalBilling === "prepay") params.set("billing", "prepay");
    return `/payment?${params.toString()}`;
  };
  const fadeIn = {
    hidden: { opacity: 0, scale: 0.98, y: 40 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { duration: 0.7, type: "spring" as const },
    },
  };
  return (
    <section className="max-w-7xl mx-auto py-12 px-4">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        className="mb-12 text-center"
      >
        <h1 className="text-4xl md:text-5xl font-extrabold mb-4 text-blue-800 drop-shadow">
          RoomRounds Monthly Pricing
        </h1>
        <p className="text-lg md:text-xl text-gray-700 max-w-2xl mx-auto">
          Simple monthly plans by facility size. Add support and maintenance at
          checkout.
        </p>
        <p className="mt-3 text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
          (Need a PO or monthly invoice? Contact{" "}
          <a
            href="mailto:sales@roomrounds.com"
            className="text-blue-600 hover:underline"
          >
            sales@roomrounds.com
          </a>
          )
        </p>
        <div className="mt-6 max-w-3xl mx-auto rounded-xl border-2 border-amber-400/90 bg-gradient-to-br from-amber-50 to-amber-100/80 px-4 py-4 shadow-md">
          <p className="text-base md:text-lg font-semibold text-amber-950 text-center leading-snug tracking-tight">
            Save 10% with 3-year prepay contracts — Ideal for budget certainty
            and maximum value.
          </p>
        </div>
      </motion.div>

      {/* <div className="flex justify-center mb-6">
        <button
          onClick={() => setFreeTrialModalOpen(true)}
          className="bg-gradient-to-r from-blue-600 to-blue-500 text-white font-semibold px-6 py-3 rounded-lg shadow hover:from-blue-700 hover:to-blue-600 transition text-lg"
        >
          Start Free Trial
        </button>
      </div> */}
      {/* MODAL for choosing plan for free trial */}
      <AnimatePresence>
        {freeTrialModalOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              setFreeTrialModalOpen(false);
              navigate("/pricing");
            }}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-lg max-w-lg w-full mx-4 p-8 relative"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute top-4 right-4 text-2xl text-gray-500 hover:text-gray-700"
                title="Close"
                onClick={() => {
                  setFreeTrialModalOpen(false);
                  navigate("/pricing");
                }}
              >
                ×
              </button>
              <h3 className="text-2xl font-bold mb-4 text-blue-800 text-center">
                Choose Your Plan
              </h3>
              <div className="flex flex-col gap-4 mb-6">
                {pricingTiers
                  .filter((tier) => tier.key !== "enterprise")
                  .map((tier) => (
                  <label
                    key={tier.key}
                    className={`flex items-center border rounded-lg p-4 cursor-pointer gap-3 transition ${
                      trialPlanKey === tier.key
                        ? "border-blue-700 bg-blue-50"
                        : "border-gray-200 hover:border-blue-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="trialPlanGroup"
                      value={tier.key}
                      checked={trialPlanKey === tier.key}
                      onChange={() => setTrialPlanKey(tier.key)}
                      className="accent-blue-700 w-5 h-5"
                    />
                    <div className="flex flex-col flex-1">
                      <span className="font-bold text-blue-700">
                        {tier.name}
                      </span>
                      <span className="text-gray-600 text-sm">
                        {tier.bedRange}
                      </span>
                    </div>
                    <span className="text-blue-700 font-semibold">
                      {tier.baseMonthly}
                    </span>
                  </label>
                ))}
              </div>
              <button
                disabled={!trialPlanKey}
                onClick={() => {
                  if (trialPlanKey) {
                    setFreeTrialModalOpen(false);
                    navigate(`/free-trial?plan=${trialPlanKey}`);
                  }
                }}
                className={`w-full bg-blue-600 text-white px-6 py-3 rounded-lg shadow font-semibold text-lg hover:bg-blue-700 transition ${
                  !trialPlanKey ? "opacity-50 pointer-events-none" : ""
                }`}
              >
                Proceed
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 lg:gap-8">
        {pricingTiers.map((tier) => (
          <motion.div
            key={tier.key}
            className={`relative bg-white rounded-2xl shadow-lg p-8 flex flex-col items-center border-2 transition-all duration-300 flex-1 ${
              tier.popular
                ? "border-blue-600 scale-105 z-10 shadow-2xl"
                : "border-blue-600 scale-105 z-10 shadow-2xl"
              // : "border-transparent hover:border-blue-300 hover:scale-105"
            }`}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={cardVariants}
          >
            {/* {tier.popular && (
              <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-bold px-4 py-1 rounded-full shadow">
                Most Popular
              </span>
            )} */}
            {tier.icon}
            <h3 className="text-2xl font-extrabold mb-1 text-blue-800 tracking-tight">
              {tier.name}
            </h3>
            <div className="text-base font-medium text-blue-500 mb-4">
              {tier.bedRange}
            </div>
            {tier.key === "enterprise" ? (
              <>
                <div className="w-full flex flex-col items-center justify-center flex-1 mb-6 min-h-[10rem] px-1">
                  <p className="text-2xl font-bold text-blue-800 text-center leading-snug">
                    Custom quotes available
                  </p>
                </div>
                <Link
                  to="/contact?pricing=true"
                  className="w-full mt-auto bg-blue-600 text-white px-6 py-2 rounded-lg shadow hover:bg-blue-700 transition text-center font-semibold"
                >
                  Contact Sales
                </Link>
              </>
            ) : (
              <>
                <div className="w-full flex flex-col items-center mb-4">
                  <>
                    {cardBilling[tier.key] === "monthly" ? (
                      <>
                        <div className="text-sm text-gray-600 mb-1">
                          Monthly
                        </div>
                        <div className="text-3xl font-extrabold text-blue-700">
                          {tier.baseMonthly}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-sm text-gray-600 mb-1">
                          3-year prepay (save 10%)
                        </div>
                        <div className="text-3xl font-extrabold text-blue-700">
                          {formatUsdWhole(
                            threeYearPrepayFromMonthly(
                              STRIPE_PLAN_USD_MONTHLY[tier.key as BillablePlanKey]
                                .base
                            )
                          )}
                        </div>
                      </>
                    )}
                    <p className="text-xs text-gray-600 mt-2 text-center max-w-[15rem] leading-snug px-1">
                      {cardBilling[tier.key] === "monthly" ? (
                        <>
                          Or pay{" "}
                          <span className="font-semibold text-blue-800">
                            {formatUsdWhole(
                              threeYearPrepayFromMonthly(
                                STRIPE_PLAN_USD_MONTHLY[
                                  tier.key as BillablePlanKey
                                ].base
                              )
                            )}
                          </span>{" "}
                          upfront for 3 years (save 10%).
                        </>
                      ) : (
                        <>
                          Equivalent to {tier.baseMonthly}/mo before discount;
                          prepay locks in 10% savings on the 3-year term.
                        </>
                      )}
                    </p>
                    <fieldset className="w-full border-0 p-0 mt-3 mb-1">
                      <legend className="sr-only">Choose billing</legend>
                      <div className="text-xs font-semibold text-gray-600 text-center mb-2">
                        Choose billing
                      </div>
                      <div className="flex justify-center gap-2 flex-wrap text-sm">
                        <label
                          className={`flex items-center gap-1.5 cursor-pointer rounded-lg border px-3 py-1.5 transition ${
                            cardBilling[tier.key] === "monthly"
                              ? "border-blue-600 bg-blue-50 shadow-sm"
                              : "border-gray-200 hover:border-blue-200"
                          }`}
                        >
                          <input
                            type="radio"
                            name={`billing-${tier.key}`}
                            checked={cardBilling[tier.key] === "monthly"}
                            onChange={() =>
                              setCardBilling((s) => ({
                                ...s,
                                [tier.key]: "monthly",
                              }))
                            }
                            className="accent-blue-700"
                          />
                          Monthly
                        </label>
                        <label
                          className={`flex items-center gap-1.5 cursor-pointer rounded-lg border px-3 py-1.5 transition ${
                            cardBilling[tier.key] === "prepay"
                              ? "border-blue-600 bg-blue-50 shadow-sm"
                              : "border-gray-200 hover:border-blue-200"
                          }`}
                        >
                          <input
                            type="radio"
                            name={`billing-${tier.key}`}
                            checked={cardBilling[tier.key] === "prepay"}
                            onChange={() =>
                              setCardBilling((s) => ({
                                ...s,
                                [tier.key]: "prepay",
                              }))
                            }
                            className="accent-blue-700"
                          />
                          3-Year Prepay
                        </label>
                      </div>
                    </fieldset>
                  </>
                </div>
                <motion.button
                  whileTap={buttonTap}
                  whileHover={buttonHover}
                  onClick={() => openModal(tier.key)}
                  className="w-full bg-blue-600 text-white px-6 py-2 rounded-lg shadow hover:bg-blue-700 transition"
                >
                  Select Plan
                </motion.button>
              </>
            )}
            {/* <p className="text-xs text-gray-500 mt-3 flex items-center justify-center gap-1">
              <FaLock className="inline text-gray-400" /> No long-term contracts. Cancel anytime.
            </p> */}
            {/* Hide extra Schedule Demo link for contact-only tiers to avoid duplication */}
            {/* {["large-medium", "large"].includes(tier.key) ? null : (
              <motion.div
                whileTap={buttonTap}
                whileHover={buttonHover}
                className="w-full mt-4"
              >
                <Link
                  to="/contact"
                  className="bg-gray-100 text-blue-700 px-6 py-2 rounded-lg shadow hover:bg-gray-200 transition w-full text-center block"
                >
                  Schedule Demo
                </Link>
              </motion.div>
            )} */}
          </motion.div>
        ))}
      </div>

      {selectedTier && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={closeModal} />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
            className="relative bg-white rounded-xl shadow-2xl max-w-lg w-[92%] p-6 z-10"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-blue-800">
                {selectedTier.name} — Plan & billing
              </h3>
              <button
                onClick={closeModal}
                aria-label="Close"
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="text-sm text-gray-600 mb-4">
              {modalBilling === "monthly"
                ? "Base monthly fee"
                : "Base package (3-year prepay, 10% off)"}
            </div>
            <div className="text-3xl font-extrabold text-blue-700 mb-4">
              {modalBilling === "monthly"
                ? selectedTier.baseMonthly
                : formatUsdWhole(
                    threeYearPrepayFromMonthly(
                      STRIPE_PLAN_USD_MONTHLY[
                        selectedTier.key as BillablePlanKey
                      ].base
                    )
                  )}
            </div>
            <div className="border-t pt-4 space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={includeMaintenance}
                  onChange={(e) => setIncludeMaintenance(e.target.checked)}
                />
                <div>
                  <div className="font-medium text-blue-800">
                    Add Maintenance Module
                  </div>
                  <div className="text-sm text-gray-600">
                    {modalBilling === "monthly" ? (
                      <>{selectedTier.maintenance} / month</>
                    ) : (
                      <>
                        {formatUsdWhole(
                          threeYearPrepayFromMonthly(
                            STRIPE_PLAN_USD_MONTHLY[
                              selectedTier.key as BillablePlanKey
                            ].maintenance
                          )
                        )}{" "}
                        upfront for 3 years (10% off vs 36× monthly)
                      </>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-1 leading-snug">
                    {modalBilling === "prepay" ? (
                      <>
                        Same module as monthly billing; this line is billed once
                        for the full prepay term (matches Stripe checkout).
                      </>
                    ) : (
                      <>
                        Plan, schedule and track facility maintenance issues.
                      </>
                    )}
                  </div>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={includeSupport}
                  onChange={(e) => setIncludeSupport(e.target.checked)}
                />
                <div>
                  <div className="font-medium text-blue-800">
                    Add Support Package
                  </div>
                  <div className="text-sm text-gray-600">
                    {modalBilling === "monthly" ? (
                      <>{selectedTier.support} / month</>
                    ) : (
                      <>
                        {formatUsdWhole(
                          threeYearPrepayFromMonthly(
                            STRIPE_PLAN_USD_MONTHLY[
                              selectedTier.key as BillablePlanKey
                            ].support
                          )
                        )}{" "}
                        upfront for 3 years (10% off vs 36× monthly)
                      </>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-1 leading-snug">
                    {modalBilling === "prepay" ? (
                      <>
                        One-time prepay for onboarding and priority support over
                        the 3-year term (matches Stripe checkout).
                      </>
                    ) : (
                      <>
                        Includes one-time setup, onboarding, and ongoing priority
                        help desk.
                      </>
                    )}
                  </div>
                </div>
              </label>
            </div>
            <fieldset className="mt-5 border border-gray-200 rounded-lg p-3">
              <legend className="text-sm font-semibold text-gray-800 px-1">
                Choose billing
              </legend>
              <div className="flex gap-3 flex-wrap mt-1">
                <label
                  className={`flex items-center gap-2 cursor-pointer text-sm rounded-md border px-3 py-2 ${
                    modalBilling === "monthly"
                      ? "border-blue-600 bg-blue-50"
                      : "border-gray-200"
                  }`}
                >
                  <input
                    type="radio"
                    name="modalBilling"
                    checked={modalBilling === "monthly"}
                    onChange={() => setModalBilling("monthly")}
                    className="accent-blue-700"
                  />
                  Monthly
                </label>
                <label
                  className={`flex items-center gap-2 cursor-pointer text-sm rounded-md border px-3 py-2 ${
                    modalBilling === "prepay"
                      ? "border-blue-600 bg-blue-50"
                      : "border-gray-200"
                  }`}
                >
                  <input
                    type="radio"
                    name="modalBilling"
                    checked={modalBilling === "prepay"}
                    onChange={() => setModalBilling("prepay")}
                    className="accent-blue-700"
                  />
                  3-Year Prepay
                </label>
              </div>
            </fieldset>
            <div className="mt-4 flex items-center justify-between gap-3">
              <div className="text-gray-700">
                {modalBilling === "monthly"
                  ? "Total per month"
                  : "3-year prepay total (10% off)"}
              </div>
              <div className="text-2xl font-bold text-blue-700 text-right">
                {modalBilling === "monthly"
                  ? formatUsdWhole(totalMonthly)
                  : formatUsdWhole(prepayThreeYearTotal)}
              </div>
            </div>
            {modalBilling === "prepay" && (
              <p className="text-xs text-gray-500 mt-1">
                One-time prepay for 36 months at 10% below month-to-month×36
                (includes selected add-ons).
              </p>
            )}
            <div className="mt-6 flex gap-3">
              <Link
                to={buildPaymentUrl()}
                onClick={closeModal}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg shadow hover:bg-blue-700 transition"
              >
                Purchase
              </Link>
              <button
                onClick={closeModal}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
            {/* <div className="mt-3 text-xs text-gray-500">
              Setup fee applies upon activation: {selectedTier.setup} (one-time).
            </div> */}
          </motion.div>
        </div>
      )}
    </section>
  );
};

export default Pricing;
